import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
  Tooltip
} from 'recharts';
import { TimelinePoint } from '../hooks/types/metricsTypes';
import { LineChart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UTM_COLORS, prettyUtmLabel } from './utils/utmSeries';
import type { VideoPitch } from '@/types/pitch';
import { pitchLabel } from '@/utils/pitches';

interface UtmRetentionSeries {
  value: string;
  retention: { [time: string]: { count: number; percent: number } };
  /** Rótulo curto para a legenda; o valor cru continua no title. */
  label?: string;
  /** Cor fixada pelo container, para não dançar quando uma série sai. */
  color?: string;
  /** Plays da origem, exibidos no chip da legenda. */
  volume?: number;
}

interface TrafficRetentionChartProps {
  timelineData: TimelinePoint[];
  isLoading: boolean;
  /** Uma linha vertical por pitch, rotulada com o nome (ou "Pitch N"). */
  pitches?: VideoPitch[];
  videoUrl?: string | null;
  utmRetentionData?: UtmRetentionSeries[];
  /** Existe dado de UTM no período, mesmo que nenhuma série esteja selecionada. */
  utmHasData?: boolean;
  /** Total de origens disponíveis, para avisar quantas ficaram de fora. */
  utmTotalCount?: number;
  /** As séries em tela foram escolhidas automaticamente (e não pelo usuário). */
  utmAutoSelection?: boolean;
  onRemoveSeries?: (value: string) => void;
  onResetSeries?: () => void;
  /** Slot à direita do título — recebe o seletor de origens. */
  utmLegendExtra?: React.ReactNode;
}

// Format time helper local
const formatTimeLabel = (value: number) => {
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Calcular ticks inteligentes para o eixo X baseado na duração do vídeo
const calculateSmartTicks = (data: TimelinePoint[]): number[] => {
  if (!data || data.length === 0) return [];
  
  const maxTime = data[data.length - 1]?.time || 0;
  const minTime = data[0]?.time || 0;
  const duration = maxTime - minTime;
  
  if (duration <= 0) return [0];
  
  // Determinar quantidade de labels baseado na duração
  let tickCount: number;
  if (duration < 300) {         // < 5 min
    tickCount = 6;
  } else if (duration < 900) {  // 5-15 min
    tickCount = 8;
  } else {                      // > 15 min
    tickCount = 10;
  }
  
  // Calcular intervalo arredondado para valores "bonitos"
  const rawInterval = duration / (tickCount - 1);
  
  let interval: number;
  if (rawInterval <= 5) interval = 5;
  else if (rawInterval <= 10) interval = 10;
  else if (rawInterval <= 15) interval = 15;
  else if (rawInterval <= 30) interval = 30;
  else if (rawInterval <= 60) interval = 60;
  else if (rawInterval <= 120) interval = 120;
  else interval = Math.ceil(rawInterval / 60) * 60;
  
  // Gerar ticks
  const ticks: number[] = [];
  for (let t = minTime; t <= maxTime; t += interval) {
    ticks.push(t);
  }
  
  // Garantir que o último ponto esteja incluído se próximo
  if (ticks.length > 0 && ticks[ticks.length - 1] < maxTime - interval * 0.3) {
    ticks.push(maxTime);
  }
  
  return ticks;
};

// Entrada do tooltip do Recharts (uma por série no ponto sob o cursor)
interface TooltipEntry {
  name: string;
  value: number;
  color: string;
}

interface PremiumRetentionTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: number;
  /** Rótulo curto por valor de UTM, montado pelo gráfico. */
  seriesLabels?: Map<string, string>;
  /** Modo UTM: rótulos longos pedem uma caixa mais larga. */
  isUtmMode?: boolean;
}

// Tooltip Premium para múltiplas UTMs
const PremiumRetentionTooltip = ({ active, payload, label, seriesLabels, isUtmMode }: PremiumRetentionTooltipProps) => {
  useLanguage();
  if (!active || !payload?.length) return null;

  // Quem está mais alto naquele instante aparece primeiro: com várias curvas
  // juntas, a ordem do payload (ordem de declaração) não ajuda a ler o gráfico.
  const orderedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));

  // Nomes de UTM são longos e pedem mais espaço mesmo quando há uma origem só;
  // a retenção geral tem uma linha curta e mantém a caixa estreita de sempre.
  const isWide = Boolean(isUtmMode);

  return (
    <div
      className={`bg-white rounded-[14px] shadow-[0_14px_28px_rgba(0,0,0,0.03)] border border-[#E4EBFF] px-5 py-3 ${
        isWide ? 'min-w-[280px]' : 'min-w-[240px]'
      }`}
      style={{ animation: 'tooltipFadeIn 120ms ease-out' }}
    >
      <p className="text-[11px] font-semibold text-[#96A0B5] uppercase tracking-wide mb-2 pb-1.5 border-b border-[#F0F3F9]">
        {translateInterfaceText("TEMPO:")}{" "}{formatTimeLabel(label)}
      </p>

      <div className="space-y-2">
        {orderedPayload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span
                className={`text-[13px] text-[#6B7280] font-medium truncate ${
                  isWide ? 'max-w-[220px]' : 'max-w-[140px]'
                }`}
                title={entry.name}
              >
                {seriesLabels?.get(entry.name) || prettyUtmLabel(entry.name) || entry.name}
              </span>
            </div>
            <span className="text-[14px] font-normal text-[#344054] shrink-0">
              {entry.value?.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export function TrafficRetentionChart({
  timelineData,
  isLoading,
  pitches = [],
  videoUrl,
  utmRetentionData,
  utmHasData,
  utmTotalCount,
  utmAutoSelection,
  onRemoveSeries,
  onResetSeries,
  utmLegendExtra
}: TrafficRetentionChartProps) {
  useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Série destacada no hover da legenda — as outras ficam apagadas.
  const [hoveredUtm, setHoveredUtm] = useState<string | null>(null);

  const isUtmMode = Boolean(utmRetentionData && utmRetentionData.length > 0);

  // Modo UTM sem NENHUM dado no período (diferente de "o usuário desmarcou tudo").
  const isUtmModeWithNoData = utmRetentionData !== undefined && utmRetentionData.length === 0 && !utmHasData;

  // Modo UTM com dados, mas com a seleção esvaziada pelo usuário.
  const isUtmModeWithEmptySelection = utmRetentionData !== undefined && utmRetentionData.length === 0 && Boolean(utmHasData);

  // Rótulo curto por valor, usado pela legenda e pelo tooltip.
  const seriesLabels = useMemo(() => {
    const map = new Map<string, string>();
    utmRetentionData?.forEach((utm) => {
      map.set(utm.value, utm.label || prettyUtmLabel(utm.value));
    });
    return map;
  }, [utmRetentionData]);

  // Transformar dados de retenção UTM para o formato do gráfico
  const chartData = useMemo(() => {
    if (!utmRetentionData || utmRetentionData.length === 0) {
      // Se não houver dados UTM, usar timelineData padrão
      return timelineData;
    }

    // Pegar todos os pontos de tempo únicos
    const allTimes = new Set<number>();
    utmRetentionData.forEach(utm => {
      Object.keys(utm.retention).forEach(timeStr => {
        const [min, sec] = timeStr.split(':').map(Number);
        const timeInSeconds = min * 60 + sec;
        allTimes.add(timeInSeconds);
      });
    });

    const sortedTimes = Array.from(allTimes).sort((a, b) => a - b);

    // Criar um array com todos os pontos de tempo e valores de cada UTM
    return sortedTimes.map(time => {
      const dataPoint: any = { time };

      utmRetentionData.forEach(utm => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        const timeKey = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        dataPoint[utm.value] = utm.retention[timeKey]?.percent || 0;
      });

      return dataPoint;
    });
  }, [utmRetentionData, timelineData]);

  // Calcular ticks inteligentes para o eixo X - PRD Premium
  const smartTicks = useMemo(() => {
    if (!utmRetentionData || utmRetentionData.length === 0) {
      return calculateSmartTicks(timelineData);
    }
    // Usar os pontos de tempo do chartData
    const times = chartData.map(d => d.time);
    if (times.length === 0) return [];
    return times.filter((_, index) => index % Math.ceil(times.length / 8) === 0 || index === times.length - 1);
  }, [chartData, timelineData, utmRetentionData]);

  // O eixo X é por categoria (os pontos da timeline, em baldes): um x que não é um
  // desses pontos some do gráfico. Cada pitch vai para o balde que o contém — o último
  // ponto que não passa do segundo dele (mesmo critério do duelo).
  const pitchLines = useMemo(() => {
    const times = chartData.map(d => Number(d.time)).filter(Number.isFinite).sort((a, b) => a - b);
    if (times.length === 0) return [];
    return pitches.map((pitch, index) => {
      let x = times[0];
      for (const t of times) {
        if (t > pitch.seconds) break;
        x = t;
      }
      return { pitch, index, x };
    });
  }, [chartData, pitches]);

  // Função para sincronizar vídeo com posição do mouse (definida depois do chartData)
  const changeVideoPosition = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current && chartData.length > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const percentage = offsetX / rect.width;
      const maxTime = chartData[chartData.length - 1].time;
      const time = percentage * maxTime;

      videoRef.current.currentTime = time > 0 ? time : 0;
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <Card className="retention-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-gradient-to-b from-white to-[#FAFAFA] shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
        <CardContent className="px-8 pt-8 pb-10">
          <div
            className="animate-pulse rounded-xl flex items-center justify-center"
            style={{
              height: 480,
              background: 'linear-gradient(180deg, #F7F9FF 0%, #FFFFFF 100%)',
              borderRadius: '16px',
              border: '1px solid #EDF1FF',
            }}
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EBF4FF] flex items-center justify-center">
                <LineChart className="h-8 w-8 text-[#4A8CFF]" />
              </div>
              <p className="text-[#6B7280] text-sm font-medium">{translateInterfaceText("Carregando dados...")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Seleção vazia: há dados de UTM no período, o usuário é que desmarcou todas.
  if (isUtmModeWithEmptySelection) {
    return (
      <Card className="retention-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-gradient-to-b from-white to-[#FAFAFA] shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
        <CardContent className="p-12 flex items-center justify-center" style={{ minHeight: 480 }}>
          <div className="text-center">
            <p className="text-[#667085] text-sm max-w-[300px] mx-auto mb-5">
              {translateInterfaceText("Escolha ao menos uma origem de tráfego para comparar as curvas de retenção.")}</p>
            {onResetSeries && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-4 text-[13px] text-[#4B7BFF] border-[#DFE7FF] bg-white hover:bg-[#F3F6FF] hover:text-[#4B7BFF] rounded-[10px]"
                onClick={onResetSeries}
              >
                {translateInterfaceText("Restaurar principais origens")}</Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty State
  // Mostrar empty state se:
  // 1. Não há dados de timeline E não há dados de UTM (modo geral sem dados)
  // 2. OU estamos no modo UTM mas não há dados de UTM
  if (
    ((!timelineData || timelineData.length === 0) && (!utmRetentionData || utmRetentionData.length === 0)) ||
    isUtmModeWithNoData
  ) {
    return (
      <Card className="retention-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-gradient-to-b from-white to-[#FAFAFA] shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
        <CardContent className="p-12 flex items-center justify-center" style={{ minHeight: 480 }}>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EBF4FF] flex items-center justify-center">
              <LineChart className="h-8 w-8 text-[#2979FF]" />
            </div>
            <h3 className="text-lg font-semibold text-[#101828] mb-2">
              {isUtmModeWithNoData
                ? translateInterfaceText("Sem dados de UTM no período")
                : translateInterfaceText("Sem dados de retenção no período")
              }
            </h3>
            <p className="text-[#667085] text-sm max-w-[280px] mx-auto">
              {isUtmModeWithNoData
                ? translateInterfaceText("Não há visualizações com parâmetros UTM neste período. Use parâmetros UTM nas suas URLs para rastrear a origem do tráfego.")
                : translateInterfaceText("Tente ampliar o período ou visualizar outro vídeo.")
              }
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hiddenSeriesCount = Math.max((utmTotalCount || 0) - (utmRetentionData?.length || 0), 0);

  // O chip pode desmontar com o cursor em cima (clique no X, refetch que derruba
  // a origem) e aí o mouseleave nunca dispara. Sem essa guarda o hover ficaria
  // preso num valor inexistente e TODAS as curvas ficariam apagadas.
  const activeHover = hoveredUtm && seriesLabels.has(hoveredUtm) ? hoveredUtm : null;

  return (
    <Card className="retention-chart-premium rounded-[16px] border border-[rgba(0,0,0,0.06)] bg-gradient-to-b from-white to-[#FAFAFA] shadow-[0_6px_24px_rgba(0,0,0,0.06)]">
      <CardContent className="px-8 pt-8 pb-10">
        {/* Header com título e, no modo UTM, a legenda em chips */}
        <div className="mb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <p className="text-[18px] font-semibold text-[#2B3343]">
              {isUtmMode
                ? translateInterfaceText("Curva de retenção por origem de tráfego")
                : translateInterfaceText("Curva de retenção ao longo do vídeo")}
            </p>
            {isUtmMode && utmLegendExtra}
          </div>

          {/* Legenda em HTML fora do SVG: com muitas origens a <Legend> do Recharts
              cresce para dentro da área do gráfico e cobre as próprias curvas. */}
          {isUtmMode && (
            <>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {utmRetentionData.map((utm, index) => {
                  const color = utm.color || UTM_COLORS[index % UTM_COLORS.length];
                  const label = seriesLabels.get(utm.value) || utm.value;

                  return (
                    <span
                      key={utm.value}
                      className="inline-flex items-center gap-1.5 h-7 pl-2 pr-1.5 rounded-full border border-[#E4EBFF] bg-white text-[12px] text-[#475067] transition-colors"
                      onMouseEnter={() => setHoveredUtm(utm.value)}
                      onMouseLeave={() => setHoveredUtm(null)}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <span className="truncate max-w-[180px]" title={utm.value}>
                        {label}
                      </span>
                      {typeof utm.volume === 'number' && utm.volume > 0 && (
                        <span className="text-[11px] text-[#9CA3AF] tabular-nums">
                          {utm.volume.toLocaleString(getInterfaceLanguage())}
                        </span>
                      )}
                      {onRemoveSeries && utmRetentionData.length > 1 && (
                        <button
                          type="button"
                          aria-label={translateInterfaceText("Remover {value0} do gráfico", { value0: (label) })}
                          className="p-0.5 rounded-full text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                          onClick={() => {
                            setHoveredUtm(null);
                            onRemoveSeries(utm.value);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>

              {hiddenSeriesCount > 0 && (
                <p className="text-[12px] text-[#6B7280] mt-2">
                  {translateInterfaceText("Mostrando")}{" "}{utmRetentionData.length} {" "}{translateInterfaceText("de")}{" "}{utmTotalCount} {" "}{translateInterfaceText("origens com curva de retenção")}{utmAutoSelection ? translateInterfaceText(" (as de maior volume)") : translateInterfaceText(" (seleção manual)")}
                  {' · '}
                  <span className="text-[#9CA3AF]">
                    {translateInterfaceText("as demais estão na tabela abaixo")}</span>
                </p>
              )}
            </>
          )}
        </div>
        
        {/* Container do gráfico com fundo premium gradiente azul */}
        <div
          ref={chartContainerRef}
          className="retention-chart-container rounded-xl overflow-hidden relative"
          style={{
            background: 'linear-gradient(180deg, #F7F9FF 0%, #FFFFFF 100%)',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid #EDF1FF',
          }}
          onMouseMove={changeVideoPosition}
        >
          {/* Vídeo posicionado por trás do gráfico */}
          {videoUrl && (
            <video
              ref={videoRef}
              src={videoUrl}
              preload="metadata"
              muted
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                objectFit: 'cover',
                pointerEvents: 'none',
                zIndex: 0,
                filter: 'brightness(0.4)'
              }}
            />
          )}

          <ResponsiveContainer width="100%" height={480} style={{ position: 'relative', zIndex: 1 }}>
            <AreaChart
              data={chartData}
              margin={{
                top: isUtmMode ? 24 : 40,
                right: 20,
                left: 0,
                bottom: 0
              }}
            >
              <defs>
                {/* Degradês para cada UTM — id sintético por índice, porque o valor
                    da UTM pode conter espaços, aspas e colchetes. */}
                {isUtmMode ? (
                  utmRetentionData.map((utm, index) => {
                    const color = utm.color || UTM_COLORS[index % UTM_COLORS.length];
                    return (
                      <linearGradient key={utm.value} id={`utmGrad-${index}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.12} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                      </linearGradient>
                    );
                  })
                ) : (
                  <linearGradient id="colorRetentionPremium" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4A8CFF" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#4A8CFF" stopOpacity={0.02} />
                  </linearGradient>
                )}
              </defs>
              
              {/* Grid Premium - azul sutil para fundo claro */}
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="rgba(75, 123, 255, 0.08)"
                vertical={false}
              />
              
              {/* Eixo X Premium - Cinza para fundo claro */}
              <XAxis
                dataKey="time"
                tickFormatter={formatTimeLabel}
                axisLine={false}
                tickLine={false}
                ticks={smartTicks}
                interval="preserveStartEnd"
                tick={{ 
                  fill: '#2d7ff9',
                  fontSize: 12,
                  fontWeight: 400,
                }}
                dy={12}
              />
              
              {/* Eixo Y Premium - Cinza para fundo claro */}
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                axisLine={{ stroke: 'rgba(75, 123, 255, 0.15)', strokeWidth: 0.5 }}
                tickLine={false}
                tick={{ 
                  fill: '#2d7ff9', 
                  fontSize: 12,
                  fontWeight: 500 
                }}
                dx={-10}
                tickFormatter={(value) => `${value}%`}
              />
              
              {/* Tooltip Premium Padronizado */}
              <Tooltip
                content={<PremiumRetentionTooltip seriesLabels={seriesLabels} isUtmMode={isUtmMode} />}
                cursor={{
                  stroke: '#4A8CFF',
                  strokeWidth: 1,
                  strokeOpacity: 1,
                  strokeDasharray: '4 4'
                }}
              />

              {/* Renderizar múltiplas áreas para UTMs ou área única para retenção geral */}
              {isUtmMode ? (
                utmRetentionData.map((utm, index) => {
                  const color = utm.color || UTM_COLORS[index % UTM_COLORS.length];
                  const isDimmed = activeHover !== null && activeHover !== utm.value;

                  return (
                    <Area
                      key={utm.value}
                      type="monotone"
                      dataKey={utm.value}
                      name={utm.value}
                      stroke={color}
                      strokeWidth={activeHover === utm.value ? 3 : 2}
                      strokeOpacity={isDimmed ? 0.15 : 1}
                      // Com duas ou mais curvas os degradês se empilham e viram
                      // uma mancha — só preenche quando há uma série sozinha.
                      fill={utmRetentionData.length === 1 ? `url(#utmGrad-${index})` : 'none'}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2 }}
                      isAnimationActive={false}
                    />
                  );
                })
              ) : (
                <Area
                  type="monotone"
                  dataKey="retention"
                  name="Retenção"
                  stroke="#4A8CFF"
                  strokeWidth={1}
                  fill="url(#colorRetentionPremium)"
                  dot={false}
                  activeDot={false}
                />
              )}

              {/* Marcação dos pitches — uma linha por pitch */}
              {pitchLines.map(({ pitch, index, x }) => (
                <ReferenceLine
                  key={pitch.id}
                  x={x}
                  stroke="#F59E0B"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{
                    value: pitchLabel(pitch, index),
                    position: 'top',
                    fill: "#F59E0B",
                    fontSize: 13,
                    fontWeight: 600,
                    offset: 10
                  }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
